import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type StaffNotification = {
  id: string;
  issue_id: string | null;
  kind: string;
  title: string;
  body: string | null;
  read_at: string | null;
  created_at: string;
};

export function SupportNotificationsBell() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const key = ["staff-notifications", user?.id] as const;
  const { data = [] } = useQuery({
    queryKey: key,
    enabled: !!user?.id,
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from("staff_notifications")
        .select("id, issue_id, kind, title, body, read_at, created_at")
        .order("created_at", { ascending: false })
        .limit(30);
      if (error) throw error;
      return (rows ?? []) as StaffNotification[];
    },
    staleTime: 15_000,
  });
  const unread = data.filter((item) => !item.read_at).length;
  const markRead = useMutation({
    mutationFn: async (ids: string[]) => {
      if (!ids.length) return;
      const { error } = await supabase
        .from("staff_notifications")
        .update({ read_at: new Date().toISOString() })
        .in("id", ids);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="relative grid h-8 w-8 place-items-center rounded-md border border-border bg-background text-foreground hover:bg-muted"
          aria-label={t("support.notifications.forYou", "For you")}
        >
          <Bell className="h-4 w-4" />
          {unread > 0 ? (
            <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white">
              {unread > 99 ? "99+" : unread}
            </span>
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[min(24rem,calc(100vw-2rem))]"
      >
        <div className="flex items-center justify-between gap-3 px-2 py-1.5">
          <DropdownMenuLabel className="p-0">
            {t("support.notifications.forYou", "For you")}
          </DropdownMenuLabel>
          {unread > 0 ? (
            <button
              type="button"
              className="text-xs font-medium text-petrol underline"
              onClick={() =>
                markRead.mutate(data.filter((n) => !n.read_at).map((n) => n.id))
              }
            >
              {t("support.notifications.markAllRead", "Mark all read")}
            </button>
          ) : null}
        </div>
        <DropdownMenuSeparator />
        {data.length ? (
          data.map((item) => (
            <div
              key={item.id}
              className="relative flex items-start rounded-sm hover:bg-accent hover:text-accent-foreground"
            >
              <Link
                to="/admin?tab=issues"
                state={{ supportTicketId: item.issue_id }}
                className="flex min-w-0 flex-1 gap-3 px-3 py-2.5 pr-10 outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span
                  className={`mt-1 h-2 w-2 shrink-0 rounded-full ${item.read_at ? "bg-transparent" : "bg-rose-500"}`}
                />
                <span className="min-w-0 flex-1">
                  <strong className="block text-sm">{item.title}</strong>
                  {item.body ? (
                    <span className="line-clamp-2 text-xs text-muted-foreground">
                      {item.body}
                    </span>
                  ) : null}
                </span>
              </Link>
              {!item.read_at ? (
                <button
                  type="button"
                  className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-background hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={t("support.notifications.markRead", "Mark read")}
                  disabled={markRead.isPending}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    markRead.mutate([item.id]);
                  }}
                >
                  <Check className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          ))
        ) : (
          <p className="px-3 py-8 text-center text-sm text-muted-foreground">
            {t("support.notifications.empty", "No assignments or mentions yet")}
          </p>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
